import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import type {
  NayaxOdataResponse,
  IrregularDeltaApiItem,
  MeterValueApiItem,
  FiltersApiResponse,
  HandleActionRequest,
  HandleActionResponse,
  BatchHandleActionRequest,
  BatchHandleActionResponse,
  UpdateCommentRequest,
  UpdateCommentResponse,
  DataErrorFilters,
} from '../types/data-error-types';

interface ODataListParams {
  skip: number;
  top: number;
  orderBy: string;
  filter: string;
  search: string;
  // count=true returns { count, rows:[] }; count=false returns { count:null, rows:[...] }
  count: boolean;
}

interface ODataFilterParams {
  filter: string;
  search: string;
}

export class DataErrorApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private getOperatorId(): number {
    const op = useOperatorStore.getState().selectedOperator;
    if (!op) throw new Error('No operator selected. Please select an operator first.');
    return op.operatorId;
  }

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const selectedOperator = useOperatorStore.getState().selectedOperator;
    if (!selectedOperator) throw new Error('No operator selected.');

    const headers = await this.tokenManager.getAuthHeaders();
    const result: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': selectedOperator.operatorId.toString(),
    };
    if (selectedOperator.appId && selectedOperator.appId !== 0) {
      result['X-APP-ID'] = selectedOperator.appId.toString();
    }
    return result;
  }

  // Builds OData $filter string from DataErrorFilters.
  // Each clause is individually wrapped in parens to match the reference URL pattern.
  static buildODataFilter(filters: DataErrorFilters): string {
    const parts: string[] = [];

    // Status: values are 'true' / 'false' — boolean literals, safe to inject directly
    if (filters.selectedStatuses.length > 0) {
      const safeStatuses = filters.selectedStatuses.filter(k => k === 'true' || k === 'false');
      if (safeStatuses.length > 0) {
        const inner = safeStatuses.map(k => `handled eq ${k}`).join(' or ');
        parts.push(`(${inner})`);
      }
    }

    // Location usage: validate against known server-side enum values before injecting
    if (filters.selectedLocationUsages.length > 0) {
      const VALID_USAGES = ['Public', 'SemiPublic', 'PrivateHouse', 'SharedBuilding'];
      const safeUsages = filters.selectedLocationUsages.filter(k => VALID_USAGES.includes(k));
      if (safeUsages.length > 0) {
        const inner = safeUsages.map(k => `usageType eq '${k}'`).join(' or ');
        parts.push(`(${inner})`);
      }
    }

    // Dates: validate ISO format before injecting (YYYY-MM-DD or full ISO string)
    const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]+)?$/;

    if (filters.periodFrom && ISO_DATE_RE.test(filters.periodFrom)) {
      const from = filters.periodFrom.includes('T')
        ? filters.periodFrom
        : `${filters.periodFrom}T00:00:00Z`;
      parts.push(`(created ge ${from})`);
    }

    if (filters.periodTo && ISO_DATE_RE.test(filters.periodTo)) {
      const to = filters.periodTo.includes('T')
        ? filters.periodTo
        : `${filters.periodTo}T23:59:59Z`;
      parts.push(`(created le ${to})`);
    }

    return parts.join(' and ');
  }

  // Rows request: count=false → returns { rows: [...], count: null }
  async getIrregularDeltaRows(
    params: Omit<ODataListParams, 'count'>
  ): Promise<NayaxOdataResponse<IrregularDeltaApiItem>> {
    const headers = await this.getAuthHeadersWithOperator();
    const operatorId = this.getOperatorId();
    const queryParts: string[] = [
      'count=false',
      `operatorId=${operatorId}`,
      `search=${encodeURIComponent(params.search)}`,
      `$skip=${params.skip}`,
      `$top=${params.top}`,
    ];
    if (params.filter) queryParts.push(`$filter=${encodeURIComponent(params.filter)}`);
    if (params.orderBy) queryParts.push(`$orderby=${encodeURIComponent(params.orderBy)}`);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardelta?${queryParts.join('&')}`,
      { method: 'GET', headers },
      'DataErrorApi',
      '/actor/irregulardelta'
    );
    return response.json();
  }

  // Count request: count=true → returns { count: N, rows: [] }
  async getIrregularDeltaCount(
    params: Pick<ODataListParams, 'filter' | 'search'>
  ): Promise<NayaxOdataResponse<IrregularDeltaApiItem>> {
    const headers = await this.getAuthHeadersWithOperator();
    const operatorId = this.getOperatorId();
    const queryParts: string[] = [
      'count=true',
      `operatorId=${operatorId}`,
      `search=${encodeURIComponent(params.search)}`,
      '$skip=0',
      '$top=1',
    ];
    if (params.filter) queryParts.push(`$filter=${encodeURIComponent(params.filter)}`);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardelta?${queryParts.join('&')}`,
      { method: 'GET', headers },
      'DataErrorApi',
      '/actor/irregulardelta (count)'
    );
    return response.json();
  }

  async getFilters(params: ODataFilterParams): Promise<FiltersApiResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const operatorId = this.getOperatorId();
    const queryParts: string[] = [`operatorId=${operatorId}`];
    if (params.filter) queryParts.push(`$filter=${encodeURIComponent(params.filter)}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardelta/filters?${queryParts.join('&')}`,
      { method: 'GET', headers },
      'DataErrorApi',
      '/actor/irregulardelta/filters'
    );
    return response.json();
  }

  async exportExcel(params: Omit<ODataListParams, 'count'>): Promise<Blob> {
    const headers = await this.getAuthHeadersWithOperator();
    const operatorId = this.getOperatorId();
    const queryParts: string[] = [
      'count=false',
      `operatorId=${operatorId}`,
      `search=${encodeURIComponent(params.search)}`,
      `$skip=${params.skip}`,
      `$top=${params.top}`,
    ];
    if (params.filter) queryParts.push(`$filter=${encodeURIComponent(params.filter)}`);
    if (params.orderBy) queryParts.push(`$orderby=${encodeURIComponent(params.orderBy)}`);

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardelta/excel?${queryParts.join('&')}`,
      { method: 'GET', headers },
      'DataErrorApi',
      '/actor/irregulardelta/excel'
    );
    return response.blob();
  }

  async getMeterValuesSurround(
    meterValueId: number,
    machineId: number,
    surroundSize: number
  ): Promise<NayaxOdataResponse<MeterValueApiItem>> {
    const headers = await this.getAuthHeadersWithOperator();
    const operatorId = this.getOperatorId();
    const queryParts = [
      `count=false`,
      `operatorId=${operatorId}`,
      `machineId=${machineId}`,
      `filterEmptyDelta=false`,
      `surroundSize=${surroundSize}`,
    ];

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/metervaluessurround/${meterValueId}?${queryParts.join('&')}`,
      { method: 'GET', headers },
      'DataErrorApi',
      `/actor/metervaluessurround/${meterValueId}`
    );
    return response.json();
  }

  async handleAction(body: HandleActionRequest): Promise<HandleActionResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardelta`,
      { method: 'POST', headers, body: JSON.stringify(body) },
      'DataErrorApi',
      '/actor/irregulardelta (POST)'
    );
    return response.json();
  }

  async batchHandleAction(body: BatchHandleActionRequest): Promise<BatchHandleActionResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardeltamany`,
      { method: 'POST', headers, body: JSON.stringify(body) },
      'DataErrorApi',
      '/actor/irregulardeltamany'
    );
    return response.json();
  }

  async updateComment(body: UpdateCommentRequest): Promise<UpdateCommentResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/actor/irregulardeltacomment`,
      { method: 'PUT', headers, body: JSON.stringify(body) },
      'DataErrorApi',
      '/actor/irregulardeltacomment'
    );
    return response.json();
  }

  // POST /api/v1/payment/rebuild-simulated-payments
  // Recalculates future payment estimates for all accounts under the operator.
  // Returns immediately — rebuild runs asynchronously on the server.
  async rebuildPayments(operatorId: number): Promise<void> {
    const headers = await this.getAuthHeadersWithOperator();
    const qs = new URLSearchParams({
      operatorId: String(operatorId),
      clearPayments: 'true',
      parallel: 'true',
    }).toString();
    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/payment/rebuild-simulated-payments?${qs}`,
      { method: 'POST', headers },
      'DataErrorApi',
      '/payment/rebuild-simulated-payments'
    );
  }
}
